import { PaginatedResponse, useApi } from '@/api';
import { EtherscanLink, Page, TableCellBottom } from '@/components';

import { useSearchParams } from '@/hooks';
import {
  CurrencyValue,
  DataTable,
  Flex,
  formatDatetime,
  Heading,
  RelativeDateTime,
  TableSkeleton,
  Text,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import _ from 'lodash';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';

type EventData = {
  datetime: string;
  order_index: string;
  block_number: number;
  tx_hash: string;
  wallet_address: string;
  event: string;
  amount: string;
  balance: string;
  sender: string;
  receiver: string;
  address: string;
  network: string;
  symbol: string;
  name: string;
};

export default function ActivityTokenPage() {
  const { network, address, tokenAddress } = useParams();
  const colors = useTheme().colors;
  const { pagination, sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });
  const [date] = useSearchParams('date', '');
  const [data, error] = useApi<PaginatedResponse<EventData[]>>({
    url: `/sparkstar/sll/tokens/${network}/${address}/${tokenAddress}/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      date: date
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Activity',
      getCell: (row) => (
        <Text color={colors[row.operation]}>{_.capitalize(row.operation)}</Text>
      ),
      sticky: true
    },
    {
      header: 'Sender',
      getCell: (row) =>
        row.sender === '0x0000000000000000000000000000000000000000' ? (
          '-'
        ) : (
          <WalletAddress
            address={row.sender}
            options={{ name: row.sender_label }}
          />
        )
    },
    {
      header: 'Receiver',
      getCell: (row) =>
        row.receiver === '0x0000000000000000000000000000000000000000' ? (
          '-'
        ) : (
          <WalletAddress
            address={row.receiver}
            options={{ name: row.receiver_label }}
          />
        )
    },

    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue value={row.amount} color={colors[row.operation]} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.amount_usd}>
          <CurrencyValue
            value={row.amount_usd}
            options={{
              currency: 'USD',
              absolute: true
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount'
    },
    {
      header: 'Transaction Hash',
      getCell: (row) => (
        <Flex flexDirection="row" gap="xs" color="muted" alignItems="center">
          <WalletAddress
            address={row.tx_hash}
            options={{ showBlockie: false }}
            color="muted"
          />
          <EtherscanLink
            value={row.tx_hash}
            network={row.network}
            type="tx"
            size="-2"
          />
        </Flex>
      )
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];
  return (
    <Page
      title={
        <Heading>
          {date ? `Activity on ${formatDatetime(date)}` : 'Activity'}
        </Heading>
      }>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => `${row.order_index}`}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    </Page>
  );
}
